/**
 * Record a flow (ticket tasks 1–4).
 *
 * Three states in one component, because they are really one flow the operator
 * moves through without leaving the Tests tab: pick where to record → drive the live
 * view while a step count climbs → review the draft and save.
 *
 * The rule this component enforces visibly, and the server enforces for real: a
 * draft with no assertion cannot be saved. The reason is shown here rather than
 * discovered from a rejected request, because an operator who does not know the rule
 * exists will otherwise stop a recording, click Save, and be told nothing useful.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, type MatrixRow, type RecordingDraft, type RecordingSummary } from '../api';

const ROLES = ['ADMIN', 'ORGANIZATION_ADMIN', 'TEACHER', 'STUDENT'] as const;

type Stage =
  | { kind: 'pick' }
  | { kind: 'recording'; recording: RecordingSummary }
  | { kind: 'review'; recording: RecordingSummary; draft: RecordingDraft };

export function RecordFlow({
  matrix,
  onClose,
  onSaved,
}: {
  matrix: MatrixRow[];
  onClose: () => void;
  onSaved: (test: { id: string; name: string }) => void;
}) {
  const [stage, setStage] = useState<Stage>({ kind: 'pick' });
  const [error, setError] = useState<string | null>(null);

  // Environments this console can actually target — the same source the run matrix
  // renders from, so "which environments exist" is never a second copy of a list.
  const environments = matrix.map((m) => m.environment).filter((e) => e !== 'prod');
  const apps = matrix[0]?.scopes.filter((s) => s.scope !== 'all') ?? [];

  const start = useCallback(async (app: string, environment: string, role: string) => {
    setError(null);
    try {
      const { recording } = await api.startRecording({ app, environment: environment as never, role });
      setStage({ kind: 'recording', recording });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);

  const stop = useCallback(async (recording: RecordingSummary) => {
    setError(null);
    try {
      const result = await api.stopRecording(recording.id);
      if (!result.draft) {
        setError(result.recording.error ?? 'The recording could not be retrieved.');
        setStage({ kind: 'pick' });
        return;
      }
      setStage({ kind: 'review', recording: result.recording, draft: result.draft });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, []);

  if (stage.kind === 'pick') {
    return (
      <Modal onClose={onClose} title="Record a flow">
        {error && <ErrorBanner text={error} />}
        <PickForm apps={apps} environments={environments} onStart={start} />
      </Modal>
    );
  }

  if (stage.kind === 'recording') {
    return (
      <Modal onClose={onClose} title="Recording" wide>
        {error && <ErrorBanner text={error} />}
        <RecordingPanel recording={stage.recording} onStop={stop} />
      </Modal>
    );
  }

  return (
    <Modal onClose={onClose} title="Review the recording" wide>
      {error && <ErrorBanner text={error} />}
      <ReviewScreen
        recording={stage.recording}
        draft={stage.draft}
        onSaved={(test) => { onSaved(test); onClose(); }}
        onError={setError}
      />
    </Modal>
  );
}

function PickForm({
  apps,
  environments,
  onStart,
}: {
  apps: Array<{ scope: string; label: string }>;
  environments: string[];
  onStart: (app: string, environment: string, role: string) => void;
}) {
  const [app, setApp] = useState(apps[0]?.scope ?? 'studio');
  const [environment, setEnvironment] = useState(environments[0] ?? 'qa');
  const [role, setRole] = useState<(typeof ROLES)[number]>('TEACHER');
  const [starting, setStarting] = useState(false);

  return (
    <div className="flex flex-col gap-4 p-5">
      <p className="text-sm" style={{ color: 'hsl(var(--text-muted))' }}>
        A real browser opens already signed in as the role you choose — you never type a
        password. Click through the flow in the live view below, mark what should be true
        with the toolbar, then review and save. <strong>Only dev and QA can be recorded</strong>{' '}
        — recording clicks and types, so it always writes.
      </p>

      <label className="flex flex-col gap-1 text-sm">
        <span className="label">Application</span>
        <select className="field" value={app} onChange={(e) => setApp(e.target.value)} data-testid="record-app">
          {apps.map((a) => <option key={a.scope} value={a.scope}>{a.label}</option>)}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="label">Environment</span>
        <select
          className="field"
          value={environment}
          onChange={(e) => setEnvironment(e.target.value)}
          data-testid="record-environment"
        >
          {environments.map((e) => <option key={e} value={e}>{e}</option>)}
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        <span className="label">Sign in as</span>
        <select
          className="field"
          value={role}
          onChange={(e) => setRole(e.target.value as (typeof ROLES)[number])}
          data-testid="record-role"
        >
          {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
      </label>

      <button
        className="btn btn-primary"
        data-testid="record-start"
        disabled={starting}
        onClick={() => { setStarting(true); onStart(app, environment, role); }}
      >
        {starting ? 'Starting…' : 'Start recording'}
      </button>
    </div>
  );
}

function RecordingPanel({
  recording,
  onStop,
}: {
  recording: RecordingSummary;
  onStop: (recording: RecordingSummary) => void;
}) {
  const [current, setCurrent] = useState(recording);
  const [stopping, setStopping] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      api.recording(current.id).then((r) => setCurrent(r.recording)).catch(() => {});
    }, 1000);
    return () => clearInterval(timer);
  }, [current.id]);

  // The iframe URL is probed before mounting, and remounted with `key` on change — the
  // same reasons runnerContainer.ts's own note gives: mounting before the container is
  // listening shows a 502 that never reloads, and a repeated port must not reuse a
  // stale, failed iframe.
  const [ready, setReady] = useState(false);
  useEffect(() => {
    if (!current.vncUrl) return;
    setReady(false);
    let cancelled = false;
    const probe = setInterval(() => {
      fetch(current.vncUrl!, { method: 'GET' })
        .then((r) => { if (!cancelled && r.ok) { setReady(true); clearInterval(probe); } })
        .catch(() => {});
    }, 1500);
    return () => { cancelled = true; clearInterval(probe); };
  }, [current.vncUrl]);

  return (
    <div className="flex flex-col gap-3 p-5">
      <div className="flex items-center gap-3">
        <span className="pill pill-warn" data-testid="record-status">{current.status}</span>
        <span className="text-sm" data-testid="record-step-count">
          {current.stepCount} step{current.stepCount === 1 ? '' : 's'} captured
        </span>
        <span className="text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
          {current.assertionCount} assertion{current.assertionCount === 1 ? '' : 's'} added
        </span>
        <button
          className="btn btn-primary ml-auto"
          data-testid="record-stop"
          disabled={stopping}
          onClick={() => { setStopping(true); onStop(current); }}
        >
          {stopping ? 'Stopping…' : 'Stop recording'}
        </button>
      </div>

      {current.assertionCount === 0 && (
        <p className="text-xs" style={{ color: 'hsl(var(--warn))' }}>
          No assertion added yet — use the toolbar in the bottom corner of the live view to
          mark something as true. A recording with none cannot be saved.
        </p>
      )}

      <div className="surface aspect-video min-h-[60vh] overflow-hidden" style={{ background: '#0b0c12' }}>
        {current.vncUrl && ready ? (
          <iframe
            key={current.vncUrl}
            src={current.vncUrl}
            title="Live view"
            className="h-full w-full"
            style={{ border: 0 }}
          />
        ) : (
          <div className="flex h-full items-center justify-center text-sm" style={{ color: 'hsl(var(--text-faint))' }}>
            Connecting to the live view…
          </div>
        )}
      </div>
    </div>
  );
}

function ReviewScreen({
  recording,
  draft,
  onSaved,
  onError,
}: {
  recording: RecordingSummary;
  draft: RecordingDraft;
  onSaved: (test: { id: string; name: string }) => void;
  onError: (message: string) => void;
}) {
  const [name, setName] = useState('');
  const [accepted, setAccepted] = useState<Set<string>>(new Set());
  const [saving, setSaving] = useState(false);
  const nameInput = useRef<HTMLInputElement>(null);

  useEffect(() => { nameInput.current?.focus(); }, []);

  const toggle = (id: string) => {
    setAccepted((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const save = async () => {
    onError('');
    setSaving(true);
    try {
      const result = await api.saveRecording(recording.id, {
        name,
        acceptedProposalIds: [...accepted],
      });
      onSaved({ id: result.test.id, name: result.test.name });
    } catch (e) {
      onError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 p-5">
      <label className="flex flex-col gap-1 text-sm">
        <span className="label">What does this test verify?</span>
        <input
          ref={nameInput}
          className="field"
          placeholder="e.g. A teacher can create a course"
          value={name}
          onChange={(e) => setName(e.target.value)}
          data-testid="record-test-name"
        />
      </label>

      {draft.assertionCount === 0 && (
        <div className="surface px-4 py-3 text-sm" style={{ borderColor: 'hsl(var(--fail) / 0.4)', background: 'hsl(var(--fail) / 0.08)', color: 'hsl(var(--fail))' }}>
          This draft has no assertions and cannot be saved. A recording reproduces what you
          did, not what should be true — go back and stop a new recording after adding at
          least one from the toolbar.
        </div>
      )}

      {draft.proposals.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold">
            Page-object replacements proposed ({draft.proposals.length})
          </h3>
          <p className="mb-2 text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
            Each is optional. Accept only what you have checked — a wrong match produces a
            test that passes while exercising something else.
          </p>
          <div className="flex flex-col gap-2">
            {draft.proposals.map((p) => (
              <label key={p.id} className="surface flex flex-col gap-1.5 p-3 text-xs" data-testid={`proposal-${p.id}`}>
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={accepted.has(p.id)}
                    onChange={() => toggle(p.id)}
                    data-testid={`accept-${p.id}`}
                  />
                  <span className="font-semibold">{p.functionName}</span>
                  <span className={`pill ${p.confidence === 'high' ? 'pill-pass' : p.confidence === 'medium' ? 'pill-warn' : 'pill-fail'}`}>
                    {p.confidence}
                  </span>
                  <span style={{ color: 'hsl(var(--text-faint))' }}>replaces {p.replaces.length} step(s)</span>
                </div>
                <ul className="ml-6 flex flex-col gap-0.5" style={{ color: 'hsl(var(--text-muted))' }}>
                  {p.reasons.map((r, i) => <li key={i}>→ {r}</li>)}
                </ul>
                {p.caveats.map((c, i) => (
                  <p key={i} style={{ color: 'hsl(var(--warn))' }}>⚠ {c}</p>
                ))}
              </label>
            ))}
          </div>
        </div>
      )}

      {draft.missing.length > 0 && (
        <div className="text-xs" style={{ color: 'hsl(var(--text-faint))' }}>
          {draft.missing.length} flow(s) recorded as raw steps because no matching action
          exists — noted for a developer.
        </div>
      )}

      <div>
        <h3 className="mb-2 text-sm font-semibold">
          Steps ({draft.steps.length}, {draft.assertionCount} assertion{draft.assertionCount === 1 ? '' : 's'})
        </h3>
        <ol className="surface flex max-h-[50vh] flex-col gap-0 overflow-auto text-xs">
          {draft.steps.map((s, i) => (
            <li
              key={i}
              className="flex items-start gap-2 px-3 py-2"
              style={{ borderBottom: '1px solid hsl(var(--border))' }}
              data-testid={`draft-step-${i}`}
            >
              <span style={{ color: 'hsl(var(--text-faint))' }}>{i + 1}.</span>
              <span className="flex-1">
                <span className={s.assertion ? 'font-semibold' : ''}>{s.functionName}</span>
                {' '}
                {Object.entries(s.params ?? {}).map(([k, v]) => `${k}=${JSON.stringify(v.value)}`).join(' ')}
                {s.selectorStrategy && (
                  <span className="ml-2" style={{ color: s.durable ? 'hsl(var(--pass))' : 'hsl(var(--warn))' }}>
                    [{s.selectorStrategy}]
                  </span>
                )}
                {s.selectorWarning && (
                  <p style={{ color: 'hsl(var(--warn))' }}>⚠ {s.selectorWarning}</p>
                )}
              </span>
            </li>
          ))}
        </ol>
      </div>

      <button
        className="btn btn-primary"
        data-testid="record-save"
        disabled={saving || draft.assertionCount === 0 || !name.trim()}
        onClick={save}
      >
        {saving ? 'Saving…' : 'Save test'}
      </button>
    </div>
  );
}

function Modal({ title, children, onClose, wide }: { title: string; children: React.ReactNode; onClose: () => void; wide?: boolean }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center overflow-auto p-6"
      style={{ background: 'hsl(var(--bg) / 0.9)' }}
      data-testid="record-modal"
    >
      <div className={`surface my-4 w-full ${wide ? 'max-w-[92vw] xl:max-w-6xl' : 'max-w-md'} p-0`}>
        <div className="flex items-baseline gap-3 p-4" style={{ borderBottom: '1px solid hsl(var(--border))' }}>
          <h2 className="text-base font-semibold">{title}</h2>
          <button className="btn btn-ghost ml-auto text-xs" onClick={onClose} data-testid="record-close">Close</button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ErrorBanner({ text }: { text: string }) {
  if (!text) return null;
  return (
    <div
      className="mx-5 mt-4 px-4 py-3 text-sm"
      style={{ borderRadius: 6, background: 'hsl(var(--fail) / 0.1)', color: 'hsl(var(--fail))' }}
      data-testid="record-error"
    >
      {text}
    </div>
  );
}
